import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Paper,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
    useCreateDoctype,
    useUpdateDoctypeData,
} from "../../../shared/hooks/useDocTypesData";

const ItemDialog = ({
    open,
    onClose,
    item,
    itemType,
    itemGroups,
    addonItems,
    onRefetch,
}) => {
    const [formData, setFormData] = useState({
        item_code: "",
        item_name: "",
        item_group: "",
        item_type: itemType || "Menu",
        standard_rate: "",
        valuation_rate: "",
        is_stock_item: 1,
        is_purchase_item: itemType === "Purchase" ? 1 : 0,
        allowed_addons: [],
        description: "",
        image: "",
    });

    const [availableAddons, setAvailableAddons] = useState([]);
    const [imagePreview, setImagePreview] = useState("");

    const createItem = useCreateDoctype();
    const updateItem = useUpdateDoctypeData();

    useEffect(() => {
        if (item) {
            // Edit mode
            const allowedAddons = item.allowed_addons
                ? typeof item.allowed_addons === "string"
                    ? JSON.parse(item.allowed_addons)
                    : item.allowed_addons
                : [];

            setFormData({
                item_code: item.item_code || "",
                item_name: item.item_name || "",
                item_group: item.item_group || "",
                item_type: item.item_type || itemType || "Menu",
                standard_rate: item.standard_rate || "",
                valuation_rate: item.valuation_rate || "",
                is_stock_item:
                    item.is_stock_item !== undefined ? item.is_stock_item : 1,
                is_purchase_item:
                    item.is_purchase_item !== undefined
                        ? item.is_purchase_item
                        : 0,
                allowed_addons: allowedAddons,
                description: item.description || "",
                image: item.image || "",
            });
            setImagePreview(item.image || "");
        } else {
            // Create mode
            setFormData({
                item_code: "",
                item_name: "",
                item_group: "",
                item_type: itemType || "Menu",
                standard_rate: "",
                valuation_rate: "",
                is_stock_item: 1,
                is_purchase_item: itemType === "Purchase" ? 1 : 0,
                allowed_addons: [],
                description: "",
                image: "",
            });
            setImagePreview("");
        }
    }, [item, itemType, open]);

    useEffect(() => {
        // Get available addons from addonItems (excluding current item if editing)
        if (addonItems) {
            const addons = addonItems.filter(
                (addonItem) => !item || addonItem.item_code !== item.item_code
            );
            setAvailableAddons(addons);
        }
    }, [addonItems, item]);

    // Auto-generate item_name from item_code in create mode
    useEffect(() => {
        if (!item && formData.item_code && !formData.item_name) {
            handleInputChange("item_name", formData.item_code);
        }
    }, [formData.item_code, item]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleAddAddon = (addon) => {
        if (addon && !formData.allowed_addons.includes(addon.item_code)) {
            setFormData((prev) => ({
                ...prev,
                allowed_addons: [...prev.allowed_addons, addon.item_code],
            }));
        }
    };

    const handleRemoveAddon = (addonCode) => {
        setFormData((prev) => ({
            ...prev,
            allowed_addons: prev.allowed_addons.filter(
                (code) => code !== addonCode
            ),
        }));
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(formData.allowed_addons);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setFormData((prev) => ({
            ...prev,
            allowed_addons: items,
        }));
    };

    const handleSubmit = async () => {
        try {
            const submitData = {
                ...formData,
                item_name: formData.item_code,
                description: formData.description || formData.item_code,
                allowed_addons: JSON.stringify(formData.allowed_addons),
            };

            if (item) {
                // Update existing item
                await updateItem.mutateAsync({
                    doctype: "Item",
                    name: item.item_code,
                    formData: submitData,
                });
                enqueueSnackbar("Item updated successfully", {
                    variant: "success",
                });
            } else {
                // Create new item
                await createItem.mutateAsync({
                    doctype: "Item",
                    value: submitData,
                });
                enqueueSnackbar("Item created successfully", {
                    variant: "success",
                });
            }

            // Trigger refetch in parent component
            if (onRefetch) {
                onRefetch();
            }

            onClose();
        } catch (error) {
            enqueueSnackbar(`Failed to ${item ? "update" : "create"} item`, {
                variant: "error",
            });
        }
    };

    const isFormValid = () => {
        if (item) {
            // Edit mode: require both item_code, item_name, and item_group
            return (
                formData.item_code && formData.item_name && formData.item_group
            );
        } else {
            // Create mode: only require item_code and item_group (item_name will be auto-set)
            return formData.item_code && formData.item_group;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {item ? "Edit Item" : "Create New Item"} - {formData.item_type}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={item ? 6 : 12}>
                        <TextField
                            fullWidth
                            label="Item Code"
                            value={formData.item_code}
                            onChange={(e) =>
                                handleInputChange("item_code", e.target.value)
                            }
                            disabled={!!item} // Disable editing for existing items
                            required
                        />
                    </Grid>
                    {item && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Item Name"
                                value={formData.item_name}
                                onChange={(e) =>
                                    handleInputChange(
                                        "item_name",
                                        e.target.value
                                    )
                                }
                                required
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            fullWidth
                            options={itemGroups}
                            getOptionLabel={(option) => option.item_group_name}
                            value={
                                itemGroups.find(
                                    (group) =>
                                        group.item_group_name ===
                                        formData.item_group
                                ) || null
                            }
                            onChange={(event, newValue) => {
                                handleInputChange(
                                    "item_group",
                                    newValue ? newValue.item_group_name : ""
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Item Group"
                                    required
                                    placeholder="Search and select item group"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            fullWidth
                            options={[
                                { value: "Menu", label: "Menu" },
                                { value: "Addons", label: "Addons" },
                            ]}
                            getOptionLabel={(option) => option.label}
                            value={{
                                value: formData.item_type,
                                label: formData.item_type,
                            }}
                            onChange={(event, newValue) => {
                                handleInputChange(
                                    "item_type",
                                    newValue ? newValue.value : "Menu"
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Item Type"
                                    placeholder="Select item type"
                                />
                            )}
                        />
                    </Grid>

                    {(formData.item_type === "Menu" ||
                        formData.item_type === "Addons") && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Standard Rate"
                                type="number"
                                value={formData.standard_rate}
                                onChange={(e) =>
                                    handleInputChange(
                                        "standard_rate",
                                        e.target.value
                                    )
                                }
                            />
                        </Grid>
                    )}

                    {formData.is_purchase_item && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Valuation Rate (Buying Price)"
                                type="number"
                                value={formData.valuation_rate}
                                onChange={(e) =>
                                    handleInputChange(
                                        "valuation_rate",
                                        e.target.value
                                    )
                                }
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) =>
                                handleInputChange("description", e.target.value)
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_stock_item === 1}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "is_stock_item",
                                            e.target.checked ? 1 : 0
                                        )
                                    }
                                />
                            }
                            label="Is Stock Item"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_purchase_item === 1}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "is_purchase_item",
                                            e.target.checked ? 1 : 0
                                        )
                                    }
                                />
                            }
                            label="Is Purchase Item"
                        />
                    </Grid>

                    {formData.item_type === "Menu" && (
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Allowed Addons
                            </Typography>

                            <Box mb={2}>
                                <Autocomplete
                                    options={availableAddons}
                                    getOptionLabel={(option) =>
                                        `${option.item_code} - ${option.item_name}`
                                    }
                                    onChange={(event, value) =>
                                        handleAddAddon(value)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Add Addon"
                                            placeholder="Search and select addons"
                                        />
                                    )}
                                />
                            </Box>

                            {formData.allowed_addons.length > 0 && (
                                <Paper elevation={1} sx={{ p: 2 }}>
                                    <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                    >
                                        Selected Addons (drag to reorder):
                                    </Typography>
                                    <DragDropContext onDragEnd={handleDragEnd}>
                                        <Droppable droppableId="addons">
                                            {(provided) => (
                                                <Box
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    display="flex"
                                                    flexWrap="wrap"
                                                    gap={1}
                                                >
                                                    {formData.allowed_addons.map(
                                                        (addonCode, index) => (
                                                            <Draggable
                                                                key={addonCode}
                                                                draggableId={
                                                                    addonCode
                                                                }
                                                                index={index}
                                                            >
                                                                {(
                                                                    provided,
                                                                    snapshot
                                                                ) => (
                                                                    <Chip
                                                                        ref={
                                                                            provided.innerRef
                                                                        }
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        label={
                                                                            addonCode
                                                                        }
                                                                        onDelete={() =>
                                                                            handleRemoveAddon(
                                                                                addonCode
                                                                            )
                                                                        }
                                                                        color="primary"
                                                                        variant={
                                                                            snapshot.isDragging
                                                                                ? "filled"
                                                                                : "outlined"
                                                                        }
                                                                        sx={{
                                                                            cursor: "grab",
                                                                            "&:active":
                                                                                {
                                                                                    cursor: "grabbing",
                                                                                },
                                                                        }}
                                                                    />
                                                                )}
                                                            </Draggable>
                                                        )
                                                    )}
                                                    {provided.placeholder}
                                                </Box>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </Paper>
                            )}
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={
                        !isFormValid() ||
                        createItem.isLoading ||
                        updateItem.isLoading
                    }
                >
                    {createItem.isLoading || updateItem.isLoading
                        ? "Saving..."
                        : item
                        ? "Update"
                        : "Create"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ItemDialog;
